import * as signalR from '@microsoft/signalr';
import { apiConfig } from '../../config/api';
import type { MessageDto } from '../api/messageService';

export type ChatConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';

export interface TypingIndicator {
  matchId: string;
  profileId: string;
  isTyping: boolean;
}

export interface MessagesReadEvent {
  matchId: string;
  readerProfileId: string;
  readAt: string;
}

export interface NewMessageNotification {
  matchId: string;
  senderName: string;
  preview: string;
  sentAt: string;
}

export interface ChatEventHandlers {
  onConnectionStateChange?: (state: ChatConnectionState) => void;
  onReceiveMessage?: (message: MessageDto) => void;
  onTypingIndicator?: (indicator: TypingIndicator) => void;
  onMessagesRead?: (event: MessagesReadEvent) => void;
  onNewMessageNotification?: (notification: NewMessageNotification) => void;
  onError?: (error: Error) => void;
}

/**
 * SignalR service specifically for chat functionality.
 */
export class ChatSignalRService {
  private connection: signalR.HubConnection | null = null;
  private connectionState: ChatConnectionState = 'disconnected';
  private accessToken: string | null = null;
  private profileId: string | null = null;
  private joinedMatches: Set<string> = new Set();
  private eventHandlers: ChatEventHandlers = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;

  constructor() {
    this.connection = null;
  }

  get state(): ChatConnectionState {
    return this.connectionState;
  }

  /** Expose token & retry count for diagnostics */
  get debugInfo() {
    return { token: this.accessToken, attempts: this.reconnectAttempts, max: this.maxReconnectAttempts };
  }

  get isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  /**
   * Set event handlers for chat events.
   */
  setEventHandlers(handlers: ChatEventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  /**
   * Connect to the chat hub.
   */
  async connect(accessToken: string, profileId: string): Promise<void> {
    if (this.connectionState === 'connected' || this.connectionState === 'connecting') {
      console.log('[ChatSignalR] Already connected or connecting');
      return;
    }

    this.accessToken = accessToken;
    this.profileId = profileId;
    this.setConnectionState('connecting');

    try {
      const hubUrl = `${apiConfig.baseUrl}/chatHub`;
      console.log('[ChatSignalR] Connecting to:', hubUrl);

      const connectionBuilder = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => accessToken,
          // Use negotiate flow for proper WebSocket connection through proxy
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
              return null; // Stop reconnecting
            }
            const delays = [0, 1000, 2000, 5000, 10000, 30000];
            return delays[Math.min(retryContext.previousRetryCount, delays.length - 1)];
          },
        })
        .configureLogging(signalR.LogLevel.Warning);

      this.connection = connectionBuilder.build();

      // Set up event handlers
      this.setupEventHandlers();

      await this.connection.start();
      this.setConnectionState('connected');
      this.reconnectAttempts = 0;
      console.log('[ChatSignalR] Connected');

      // Register profile with the hub
      await this.registerProfile(profileId);

      // Rejoin any matches we were in
      for (const matchId of this.joinedMatches) {
        await this.joinMatch(matchId);
      }
    } catch (error) {
      console.error('[ChatSignalR] Connection failed:', error);
      this.setConnectionState('error');
      this.eventHandlers.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Disconnect from the chat hub.
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.setConnectionState('disconnected');
      this.joinedMatches.clear();
    }
  }

  /**
   * Register the user's profile with the hub.
   */
  private async registerProfile(profileId: string): Promise<void> {
    if (!this.connection || !this.isConnected) return;

    try {
      const result = await this.connection.invoke<boolean>('RegisterProfile', profileId);
      if (result) {
        console.log('[ChatSignalR] Profile registered:', profileId);
      }
    } catch (error) {
      console.error('[ChatSignalR] Failed to register profile:', error);
    }
  }

  /**
   * Join a match conversation to receive real-time messages.
   */
  async joinMatch(matchId: string): Promise<boolean> {
    if (!this.connection || !this.isConnected) {
      // Store for rejoining after reconnect
      this.joinedMatches.add(matchId);
      return false;
    }

    try {
      const result = await this.connection.invoke<boolean>('JoinMatch', matchId);
      if (result) {
        this.joinedMatches.add(matchId);
        console.log('[ChatSignalR] Joined match:', matchId);
      }
      return result;
    } catch (error) {
      console.error('[ChatSignalR] Failed to join match:', error);
      return false;
    }
  }

  /**
   * Leave a match conversation.
   */
  async leaveMatch(matchId: string): Promise<void> {
    this.joinedMatches.delete(matchId);

    if (!this.connection || !this.isConnected) return;

    try {
      await this.connection.invoke('LeaveMatch', matchId);
      console.log('[ChatSignalR] Left match:', matchId);
    } catch (error) {
      console.error('[ChatSignalR] Failed to leave match:', error);
    }
  }

  /**
   * Send a message through SignalR.
   */
  async sendMessage(
    matchId: string,
    content: string,
    type: string = 'text',
    attachmentUrl?: string,
    replyToMessageId?: string
  ): Promise<{ success: boolean; message?: MessageDto; error?: string }> {
    if (!this.connection || !this.isConnected) {
      return { success: false, error: 'Not connected' };
    }

    try {
      const result = await this.connection.invoke<{
        success: boolean;
        message?: MessageDto;
        error?: string;
      }>('SendMessage', {
        matchId,
        content,
        type,
        attachmentUrl,
        replyToMessageId,
      });
      return result;
    } catch (error) {
      console.error('[ChatSignalR] Failed to send message:', error);
      return { success: false, error: (error as Error).message };
    }
  }

  /**
   * Mark messages as read.
   */
  async markAsRead(matchId: string): Promise<boolean> {
    if (!this.connection || !this.isConnected) return false;

    try {
      return await this.connection.invoke<boolean>('MarkAsRead', matchId);
    } catch (error) {
      console.error('[ChatSignalR] Failed to mark as read:', error);
      return false;
    }
  }

  /**
   * Send typing indicator.
   */
  async sendTypingIndicator(matchId: string, isTyping: boolean): Promise<void> {
    if (!this.connection || !this.isConnected) return;

    try {
      await this.connection.invoke('SendTypingIndicator', matchId, isTyping);
    } catch (error) {
      // Silently fail - typing indicators are not critical
    }
  }

  /**
   * Get unread message count.
   */
  async getUnreadCount(): Promise<number> {
    if (!this.connection || !this.isConnected) return 0;

    try {
      return await this.connection.invoke<number>('GetUnreadCount');
    } catch (error) {
      console.error('[ChatSignalR] Failed to get unread count:', error);
      return 0;
    }
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Handle incoming messages
    this.connection.on('ReceiveMessage', (message: MessageDto) => {
      console.log('[ChatSignalR] Received message:', message.id);
      this.eventHandlers.onReceiveMessage?.(message);
    });

    // Handle typing indicators
    this.connection.on('TypingIndicator', (indicator: TypingIndicator) => {
      this.eventHandlers.onTypingIndicator?.(indicator);
    });

    // Handle messages read
    this.connection.on('MessagesRead', (event: MessagesReadEvent) => {
      this.eventHandlers.onMessagesRead?.(event);
    });

    // Handle new message notifications (when not in the match group)
    this.connection.on('NewMessageNotification', (notification: NewMessageNotification) => {
      this.eventHandlers.onNewMessageNotification?.(notification);
    });

    // Connection events
    this.connection.onreconnecting((error) => {
      console.log('[ChatSignalR] Reconnecting...', error);
      this.setConnectionState('reconnecting');
    });

    this.connection.onreconnected((connectionId) => {
      console.log('[ChatSignalR] Reconnected:', connectionId);
      this.setConnectionState('connected');
      this.reconnectAttempts = 0;

      // Re-register profile and rejoin matches
      if (this.profileId) {
        this.registerProfile(this.profileId);
      }
      for (const matchId of this.joinedMatches) {
        this.joinMatch(matchId);
      }
    });

    this.connection.onclose((error) => {
      console.log('[ChatSignalR] Connection closed', error);
      this.setConnectionState('disconnected');
      if (error) {
        this.eventHandlers.onError?.(error);
      }
    });
  }

  private setConnectionState(state: ChatConnectionState): void {
    this.connectionState = state;
    this.eventHandlers.onConnectionStateChange?.(state);
  }
}

// Singleton instance
export const chatSignalRService = new ChatSignalRService();
